import React, { memo, useEffect, useRef, useState } from 'react';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import CustomDragPreviewFactory from 'components/tables/dndStandardTable/tableComponents/CustomDragPreviewFactory';
import DraggableRow from 'components/tables/dndStandardTable/tableComponents/DraggableRow';
import TableHeadCell from 'components/tables/dndStandardTable/tableComponents/TableHeadCell';

import { useStyles } from './tableComponents/styles';
import TPagination from './tableComponents/TablePagination';
import { type RowData, type StandardTableProps } from './types';

const StandardTable = <T extends RowData>({
  cellConfig,
  columns,
  customRowCellPreview,
  customTableRowPreview,
  data,
  id,
  onCellClick,
  onHeaderCellClick,
  onCellContextMenu,
  onDragStart,
  onDrop,
  onDragEnd,
  onRowClick,
  onRowMouseOut,
  onRowMouseOver,
  pageableConfig,
  rowConfig,
  sortableConfig,
  tableConfig,
  containerComponent = Paper,
  clickable,
  hover,
  selectedRows,
}: StandardTableProps<T>) => {
  const { classes } = useStyles();
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const [paperDiv, setPaperDiv] = useState<Element | null>(null);
  const paperComputedStyle = paperDiv ? getComputedStyle(paperDiv) : undefined;
  const previewBackgroundColor = paperComputedStyle?.backgroundColor || '#ffffff';
  const previewWidth = paperComputedStyle?.width || '100%';
  const renderHeader = !!columns?.find((column) => column?.getTitle);
  useEffect(() => {
    if (paperRef && headerRowRef) {
      setPaperDiv(paperRef?.current);
    }
  }, [paperRef, headerRowRef]);

  // marginLeft fixes custom preview drag handle issue
  const previewStyle = {
    backgroundColor: previewBackgroundColor,
    width: previewWidth,
  };
  return (
    <>
      <TableContainer
        ref={paperRef}
        className={tableConfig?.className ? tableConfig.className : ''}
        component={containerComponent}
      >
        <Table className={classes.participantsTable} id={id}>
          {renderHeader && (
            <TableHead>
              <TableRow ref={headerRowRef}>
                {columns.map((column, index) => (
                  <TableHeadCell
                    key={column.key}
                    cellConfig={cellConfig}
                    column={column}
                    index={index}
                    sortableConfig={sortableConfig}
                    onCellClick={onHeaderCellClick}
                  />
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {data?.map((rowItem, index) => (
              <DraggableRow
                key={rowItem.id}
                cellConfig={cellConfig}
                clickable={clickable}
                columns={columns}
                customClassName={rowItem.className}
                hover={hover}
                index={index}
                rowConfig={rowConfig}
                rowItem={rowItem}
                selected={selectedRows?.includes(rowItem.id)}
                onCellClick={onCellClick}
                onContextMenu={onCellContextMenu}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                onDrop={onDrop}
                onRowClick={onRowClick}
                onRowMouseOut={onRowMouseOut}
                onRowMouseOver={onRowMouseOver}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pageableConfig ? (
        <TPagination
          count={pageableConfig.count}
          labelDisplayedRows={pageableConfig.labelDisplayedRows}
          page={pageableConfig.page}
          rowsPerPage={pageableConfig.rowsPerPage}
          rowsPerPageOptions={pageableConfig.rowsPerPageOptions}
          onPageChange={pageableConfig.onPageChange}
          onRowsPerPageChange={pageableConfig.onRowsPerPageChange}
        />
      ) : null}
      <CustomDragPreviewFactory
        cellConfig={cellConfig}
        columns={columns}
        customRowCellPreview={customRowCellPreview}
        customTableRowPreview={customTableRowPreview}
        previewStyle={previewStyle}
        rowConfig={rowConfig}
      />
    </>
  );
};

/**
 * @deprecated use EnhancedTable instead
 */
export default memo(StandardTable) as typeof StandardTable;
